// The Errors screen: grouped issues with resolve/ignore, and an issue page with the readable
// (source-mapped) stack and recent occurrences. Kept apart from app.ts, which only routes here.
import type { ErrorFrame, ErrorGroup, ErrorIssue, ErrorStatus } from "../src/types";
import type { AttemptView, IssueAutofixView } from "@monoblock/autofix/src/types";

export interface ErrorsContext {
  el: HTMLElement;
  /** project, from, to for the current range. */
  params: Record<string, string>;
  /** False once the user has moved on, so late responses are dropped. */
  current: () => boolean;
  mobile: boolean;
}

type StatusTab = ErrorStatus | "all";
let tab: StatusTab = "open";
let issue: string | null = null;
let showAll = false; // library frames on the issue page
let ctx: ErrorsContext | null = null;
// Alert emails link to /project/<id>/errors/?fp=<fingerprint> (older ones to
// ?screen=errors&project=<id>&fp=…). app.ts rewrites the URL without fp, so read it once at load
// and open that issue on the first render of its project.
const landing = new URLSearchParams(location.search);
const landingPath = location.pathname.match(/^\/project\/([^/]+)\/errors\/?$/);
let linked = landingPath
  ? { project: decodeURIComponent(landingPath[1]!), fp: landing.get("fp") }
  : landing.get("screen") === "errors"
    ? { project: landing.get("project"), fp: landing.get("fp") }
    : null;

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const fmtN = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : Math.round(n).toLocaleString("en-US"));
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function ago(ms: number): string {
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 30 * 86_400) return `${Math.floor(s / 86_400)}d ago`;
  const d = new Date(ms);
  return `${d.getDate()} ${MON[d.getMonth()]}`;
}
const when = (ms: number) => new Date(ms).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const skeleton = (h: number) => `<div class="skeleton" style="height:${h}px"></div>`;
const card = (title: string, body: string, extra = "") =>
  `<section class="card"><div class="card-head"><h2>${title}</h2><span class="spacer"></span>${extra}</div>${body}</section>`;
const plural = (n: number, w: string) => `${fmtN(n)} ${w}${n === 1 ? "" : "s"}`;
/** "webpack://app/./src/a.ts", "../../src/a.ts" -> "src/a.ts" */
const srcPath = (s: string) => s.replace(/^[a-z-]+:\/\/[^/]*\//i, "").replace(/^(\.{1,2}\/)+/, "");
const STATUS_PILL: Record<ErrorStatus, string> = { open: "poor", resolved: "good", ignored: "none" };
const pathOf = (url: string | null) => {
  if (!url) return "–";
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

async function api<T>(path: string, p: Record<string, string>): Promise<T> {
  const res = await fetch(`/api/${path}?${new URLSearchParams(p)}`, { credentials: "same-origin" });
  const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  return body as T;
}

async function setStatus(fp: string, status: ErrorStatus): Promise<void> {
  const res = await fetch(`/api/errors/${encodeURIComponent(fp)}`, {
    method: "PATCH",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ project: ctx!.params.project, status }),
  });
  if (!res.ok) throw new Error(((await res.json().catch(() => ({}))) as { error?: string }).error ?? `HTTP ${res.status}`);
}

const AUTOFIX_ERRORS: Record<string, string> = {
  github_token_missing: "The GitHub token isn't set up on MonoLog yet (GITHUB_DISPATCH_TOKEN).",
  no_repo: "Set the site's GitHub repository in Settings → Site settings → Auto-fix first.",
  autofix_off: "Auto-fix is off for this site (Settings → Site settings → Auto-fix).",
  in_progress: "A fix is already in progress.",
  already_fixed: "A pull request is already open for this error.",
  read_only: "Only owners of this site can ask for a fix.",
  not_allowed: "Only owners of this site can ask for a fix.",
};
/** The last Fix it problem, shown on the issue page until another issue is opened. */
let autofixMsg: string | null = null;

async function requestFix(fp: string): Promise<void> {
  const res = await fetch(`/api/errors/${encodeURIComponent(fp)}/autofix?${new URLSearchParams({ project: ctx!.params.project ?? "" })}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
  const out = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  autofixMsg = res.ok ? null : (out.message ?? AUTOFIX_ERRORS[out.error ?? ""] ?? `Couldn't ask for a fix (${out.error ?? `HTTP ${res.status}`}).`);
}

const fixLabel = (a: AttemptView) =>
  a.status === "pr_opened" ? "Pull request opened" : a.status === "issue_opened" ? "Issue opened" : a.status === "no_fix" ? "No fix found" : a.status === "failed" ? "Failed" : "";

/** The Auto-fix card: Fix it, and Requested → Sent → PR opened / Issue opened / Failed, with times. */
function autofixCard(af: IssueAutofixView | undefined, fp: string, groupStatus: ErrorStatus = "open"): string {
  if (!af || (af.mode === "off" && !af.attempt)) return "";
  const a = af.attempt;
  const btn = af.can_request
    ? `<button class="btn primary" data-err-fix="${esc(fp)}"${af.token_configured ? "" : " disabled"}>${a && a.status !== "suggested" ? "Try again" : "Fix it"}</button>`
    : "";
  const step = (done: boolean, label: string, at: number | null) =>
    `<li class="af-step${done ? " done" : ""}"><span class="af-dot" aria-hidden="true"></span><span class="af-what">${label}</span>${at ? `<span class="muted" title="${esc(new Date(at).toLocaleString())}">${when(at)}</span>` : ""}</li>`;
  let body: string;
  if (!a || a.status === "suggested") {
    body = `<p class="muted af-intro">${
      af.repo
        ? `Claude can look into this error in <span class="mono">${esc(af.repo)}</span>, in GitHub Actions, and open a pull request. Nothing is merged automatically.`
        : "Set the site's GitHub repository in Settings → Site settings → Auto-fix."
    }${a ? ` Suggested in Ping ${ago(a.created_at)}.` : ""}</p>`;
  } else {
    const result = a.finished_at && a.status !== "requested" && a.status !== "dispatched";
    const link = a.result_url
      ? ` <a href="${esc(a.result_url)}" target="_blank" rel="noopener noreferrer">${a.status === "pr_opened" ? "Open pull request" : a.status === "issue_opened" ? "Open issue" : "Open on GitHub"}</a>`
      : "";
    const who = a.trigger === "auto" ? ` <span class="muted">automatically</span>` : a.requested_by ? ` <span class="muted">by ${esc(a.requested_by)}</span>` : "";
    body = `<ol class="af-steps">
      ${step(true, `Requested${who}`, a.requested_at)}
      ${a.dispatched_at ? step(true, `Sent to <span class="mono">${esc(a.repo ?? "")}</span>`, a.dispatched_at) : ""}
      ${result ? step(true, `<b class="af-${a.status}">${fixLabel(a)}</b>${link}`, a.finished_at) : a.status === "dispatched" ? step(false, "Claude is working on it…", null) : ""}
      ${a.resolved_at ? step(true, `<b class="af-pr_opened">Resolved after fix</b> <span class="muted">no events for 3 days</span>`, a.resolved_at) : ""}
      ${a.regressed_at ? step(true, `<b class="af-failed">Fix didn't hold</b> <span class="muted">it came back</span>`, a.regressed_at) : ""}
    </ol>${a.error ? `<div class="err-note">${esc(a.error)}</div>` : ""}${a.summary ? `<blockquote class="af-summary">${esc(a.summary)}</blockquote>` : ""}${
      a.status === "no_fix" && groupStatus === "open"
        ? `<p class="af-intro">Nothing in the site's code to fix, so it's likely noise. <button class="btn" data-err-set="ignored" data-err-fp="${esc(fp)}">Ignore this error</button></p>`
        : ""
    }`;
  }
  const notes = [
    !af.token_configured && af.can_request ? `<div class="err-note">The GitHub token isn't set up on MonoLog yet (GITHUB_DISPATCH_TOKEN), so Fix it can't reach GitHub.</div>` : "",
    autofixMsg ? `<div class="err-note">${esc(autofixMsg)}</div>` : "",
  ].join("");
  return card("Auto-fix", `${body}${notes}`, btn);
}

/** Entry point from app.ts. */
export async function renderErrors(c: ErrorsContext): Promise<void> {
  if (ctx && ctx.params.project !== c.params.project) issue = null;
  if (linked) {
    if (linked.fp && linked.project === c.params.project) issue = linked.fp;
    linked = null;
  }
  ctx = c;
  return issue ? renderIssue(c, issue) : renderList(c);
}

const actions = (fp: string, status: ErrorStatus, big = false) => {
  const b = (label: string, to: ErrorStatus) => `<button class="${big ? "btn" : "link"}" data-err-set="${to}" data-err-fp="${esc(fp)}">${label}</button>`;
  return status === "open" ? `${b("Resolve", "resolved")}${b("Ignore", "ignored")}` : b(status === "resolved" ? "Reopen" : "Stop ignoring", "open");
};

async function renderList(c: ErrorsContext): Promise<void> {
  const head = (counts?: Record<ErrorStatus, number>) => {
    const n = (s: ErrorStatus) => (counts ? ` <span class="err-n">${fmtN(counts[s])}</span>` : "");
    const tabs: [string, StatusTab][] = [[`Open${n("open")}`, "open"], [`Resolved${n("resolved")}`, "resolved"], [`Ignored${n("ignored")}`, "ignored"], ["All", "all"]];
    return `<div class="err-head"><h1>Errors</h1><span class="spacer"></span><div class="seg small" role="group">${tabs
      .map(([l, v]) => `<button data-err-tab="${v}" aria-pressed="${v === tab}">${l}</button>`)
      .join("")}</div></div>`;
  };
  c.el.innerHTML = `${head()}${card("Issues", skeleton(240))}`;
  const r = await api<{ groups: ErrorGroup[]; counts: Record<ErrorStatus, number> }>("errors", { ...c.params, status: tab });
  if (!c.current()) return;

  const empty =
    tab === "open"
      ? "No open errors in this range. JavaScript errors are captured automatically by the tracking script."
      : "Nothing here for this range.";
  const rows = r.groups.length
    ? `<div class="err-list">${c.mobile ? "" : `<div class="err-row err-rh"><span>Issue</span><span>Events</span><span>Users</span><span>First seen</span><span>Last seen</span><span>Release</span><span></span></div>`}${r.groups
        .map((g) =>
          c.mobile
            ? `<div class="err-row" data-err-open="${esc(g.fingerprint)}">
                <div class="err-title"><b>${esc(g.type)}</b>${g.third_party ? ` <span class="pill" title="Every frame is Shopify\u2019s own hosted code">Third-party</span>` : ""}${tab === "all" ? ` <span class="pill ${STATUS_PILL[g.status]}">${g.status}</span>` : ""}</div>
                <div class="err-msg">${esc(g.message)}</div>
                <div class="err-meta">${plural(g.count, "event")} · ${plural(g.users, "user")} · ${ago(g.last_seen)}${g.last_release ? ` · ${esc(g.last_release)}` : ""}</div>
              </div>`
            : `<div class="err-row" data-err-open="${esc(g.fingerprint)}">
                <div class="err-issue"><div class="err-title"><b>${esc(g.type)}</b>${g.third_party ? ` <span class="pill" title="Every frame is Shopify\u2019s own hosted code">Third-party</span>` : ""}${tab === "all" ? ` <span class="pill ${STATUS_PILL[g.status]}">${g.status}</span>` : ""}</div><div class="err-msg">${esc(g.message)}</div></div>
                <span class="n p">${fmtN(g.count)}</span><span class="n">${fmtN(g.users)}</span>
                <span class="n" title="${when(g.first_seen)}">${ago(g.first_seen)}</span><span class="n" title="${when(g.last_seen)}">${ago(g.last_seen)}</span>
                <span class="err-rel mono" title="${esc(g.last_release ?? "")}">${esc(g.last_release ?? "–")}</span>
                <span class="err-act">${actions(g.fingerprint, g.status)}</span>
              </div>`,
        )
        .join("")}</div>`
    : `<div class="empty">${empty}</div>`;
  c.el.innerHTML = `${head(r.counts)}${card("Issues", rows, `<span class="hint">${r.groups.length === 200 ? "Latest 200" : ""}</span>`)}`;
}

function frameHtml(f: ErrorFrame): string {
  const min = `${esc(f.file)}${f.line !== null ? `:${f.line}:${f.col}` : ""}`;
  if (f.original) {
    const o = f.original;
    return `<li class="err-frame app"><div><b>${esc(o.name ?? f.fn)}</b> <span class="mono">${esc(srcPath(o.source))}:${o.line}:${o.col}</span></div>${
      o.context ? `<code>${esc(o.context)}</code>` : ""
    }<div class="err-min mono">${min}</div></li>`;
  }
  return `<li class="err-frame ${f.in_app ? "app" : "lib"}"><div><b>${esc(f.fn)}</b> <span class="mono">${min}</span></div></li>`;
}

async function renderIssue(c: ErrorsContext, fp: string): Promise<void> {
  const back = `<button class="link err-back" data-err-back>← All errors</button>`;
  c.el.innerHTML = `${back}${card("…", skeleton(120))}${card("Stack trace", skeleton(200))}`;
  const r = await api<ErrorIssue>("error", { project: c.params.project ?? "", fp });
  if (!c.current()) return;
  const g = r.group;

  const stats: [string, string][] = [
    ["Events", fmtN(g.count)],
    ["Users", fmtN(g.users)],
    ["First seen", ago(g.first_seen)],
    ["Last seen", ago(g.last_seen)],
    ["Release", g.last_release ?? "–"],
  ];
  const summary = `<section class="card err-sum">
      <div class="err-sum-head"><div class="err-title"><b>${esc(g.type)}</b>${g.third_party ? ` <span class="pill" title="Every frame is Shopify\u2019s own hosted code">Third-party</span>` : ""} <span class="pill ${STATUS_PILL[g.status]}">${g.status}</span></div>
        <span class="spacer"></span><div class="btns">${actions(g.fingerprint, g.status, true)}</div></div>
      <p class="err-msg big">${esc(g.message)}</p>
      ${g.sample_url ? `<div class="err-meta">Latest on <span class="mono">${esc(pathOf(g.sample_url))}</span></div>` : ""}
      <div class="err-stats">${stats.map(([k, v]) => `<div><span>${k}</span><b title="${esc(v)}">${esc(v)}</b></div>`).join("")}</div>
    </section>`;

  const hidden = r.frames.filter((f) => !f.in_app).length;
  const frames = showAll ? r.frames : r.frames.filter((f) => f.in_app);
  const note = !r.frames.length
    ? ""
    : r.symbolicated
      ? `<div class="err-note good">Mapped to source with the ${esc(r.release ?? "")} source maps.</div>`
      : `<div class="err-note">${r.release ? `No source maps uploaded for release <span class="mono">${esc(r.release)}</span>, so this is the minified stack.` : `This error has no release, so source maps can't be applied. Add <span class="mono">data-release</span> to the script tag.`}</div>`;
  const stack = r.frames.length
    ? `${note}<ol class="err-frames">${frames.map(frameHtml).join("") || `<li class="empty">No frames from this site's own code.</li>`}</ol>`
    : r.raw_stack
      ? `<pre class="err-raw">${esc(r.raw_stack)}</pre>`
      : `<div class="empty">No stack trace was sent with this error.</div>`;
  const toggle = hidden ? `<button class="link" data-err-frames>${showAll ? "Only this site's code" : `Show all frames (${hidden} more)`}</button>` : "";

  const occ = r.occurrences.length
    ? `<div class="err-list">${r.occurrences
        .map(
          (o) => `<div class="err-occ"><span class="n">${when(o.ts)}</span><span class="mono err-path" title="${esc(o.url ?? "")}">${esc(pathOf(o.url))}</span>
            <span class="err-meta">${[o.browser, o.os, o.device, o.country, o.release].filter(Boolean).map((x) => esc(x!)).join(" · ")}</span></div>`,
        )
        .join("")}</div>`
    : `<div class="empty">Individual occurrences are kept for 30 days.</div>`;

  c.el.innerHTML = `${back}${summary}${autofixCard(r.autofix, g.fingerprint, g.status)}${card("Stack trace", stack, toggle)}${card("Recent occurrences", occ, `<span class="hint">Latest ${r.occurrences.length}</span>`)}`;
}

function rerender(): void {
  if (ctx) void renderErrors(ctx).catch(showError);
}
function showError(err: unknown): void {
  ctx?.el.insertAdjacentHTML("afterbegin", `<div class="notice error">${esc(`Couldn't load errors: ${(err as Error).message}`)}</div>`);
}

document.addEventListener("click", (ev) => {
  const t = ev.target as HTMLElement;
  if (!ctx || !t.closest || !ctx.el.contains(t)) return;
  const set = t.closest<HTMLElement>("[data-err-set]");
  if (set) {
    ev.stopPropagation();
    return void setStatus(set.dataset.errFp!, set.dataset.errSet as ErrorStatus).then(rerender, showError);
  }
  const fix = t.closest<HTMLButtonElement>("[data-err-fix]");
  if (fix) {
    ev.stopPropagation();
    fix.disabled = true;
    fix.textContent = "Asking…";
    return void requestFix(fix.dataset.errFix!).then(rerender, showError);
  }
  const tb = t.closest<HTMLElement>("[data-err-tab]");
  if (tb) return void ((tab = tb.dataset.errTab as StatusTab), rerender());
  if (t.closest("[data-err-back]")) return void ((issue = null), rerender());
  if (t.closest("[data-err-frames]")) return void ((showAll = !showAll), rerender());
  const open = t.closest<HTMLElement>("[data-err-open]");
  if (open) return void ((issue = open.dataset.errOpen!), (autofixMsg = null), (showAll = false), scrollTo(0, 0), rerender());
});
